import { Text } from "@dropbox/dig-components/typography";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_MessageAction } from "@mirage/analytics/events/types/click_message_action";
import { PAP_Shown_AssistantResponse } from "@mirage/analytics/events/types/shown_assistant_response";
import { AllSourcesList } from "@mirage/mosaics/Chat/components/chat/AllSourcesList";
import { AllSourcesTiles } from "@mirage/mosaics/Chat/components/chat/AllSourcesTiles";
import { ConversationMessageActions } from "@mirage/mosaics/Chat/components/chat/ConversationMessageActions";
import { ConversationMessageRowActionAndText } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ConversationMessageRowActionAndText";
import {
  ConversationMessageRowMessageSourceRow,
  ConversationMessageSource,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ConversationMessageSource";
import { useIsSingleLine } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/sizingHooks";
import type {
  LayoutVariant,
  ProgressStepDisplayState,
  RenderMessageActions,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/types";
import { useStepMessageTransitionClass } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/useProgressHandling";
import { ConversationFeedbackModal } from "@mirage/mosaics/Chat/components/chat/feedback/ConversationFeedbackModal";
import type { MarkdownResponseProps } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import {
  composeSourceToMirage,
  getSourceRowParts,
} from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import { getCitationAndContextSources } from "@mirage/mosaics/Chat/utils/messageSources";
import { AnswersAnnotationModal } from "@mirage/mosaics/SearchPage/Annotations/AnswersAnnotationModal";
import type { SearchResult } from "@mirage/service-dbx-api";
import {
  useFeatureFlagValue,
  useFeatureFlagValueBool,
} from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import { copyToClipboard } from "@mirage/service-platform-actions";
import type { QuestionAndAnswerSource } from "@mirage/shared/answers/multi-answer";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { meticulousRedactedStringClass } from "@mirage/translations";
import classNames from "classnames";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import type {
  ChatConversationMessage,
  ChatConversationMessageInstruction,
  ChatConversationMessageMessage,
  ChatSource,
} from "../../../types";
import { ChatSourceType } from "../../../types";
import styles from "./ConversationMessages.module.css";

const logger = tagged("ConversationMessages/ConversationMessageRow");

export interface ConversationMessageRowGenericProps<MessageType> {
  variant: LayoutVariant;
  message: MessageType;
  onRemoveSource: (source: ChatSource) => void;
  isLastMessage: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  renderActions?: RenderMessageActions;
  onOpenArtifact: () => void;
  showReactions?: boolean;
  hideSources?: boolean; // hide sources in the message row, default to false
  progressStepState?: ProgressStepDisplayState;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
  spanId?: string;
  lastUserMessage?: ChatConversationMessage;
  searchResultsCache?: React.MutableRefObject<
    Map<string, Promise<"hidden" | "top" | "bottom">>
  >;
  overrideMarkdownRenderer?: (props: MarkdownResponseProps) => React.ReactNode;
  showDraftGeneratedButton?: boolean;
  isWaitingForResponse?: boolean;
}

export type ConversationMessageRowProps =
  ConversationMessageRowGenericProps<ChatConversationMessage>;
export const ConversationMessageRow = memo(
  ({ message, ...props }: ConversationMessageRowProps) => {
    switch (message.type) {
      case "message":
        return <ConversationMessageRowMessage message={message} {...props} />;
      case "instruction":
        return (
          <ConversationMessageRowInstruction message={message} {...props} />
        );
      default:
        message satisfies never;
        throw new Error(`Unknown message type: ${message}`);
    }
  },
);
ConversationMessageRow.displayName = "ConversationMessageRow";

interface ConversationMessageRowInstructionProps {
  message: ChatConversationMessageInstruction;
  variant: LayoutVariant;
}

/**
 * MessageRow implementation for an Instruction message.
 */
export const ConversationMessageRowInstruction = memo(
  ({ message, variant }: ConversationMessageRowInstructionProps) => {
    return (
      <div
        className={classNames(styles.ConversationMessageRow, {
          [styles.ConversationMessageRowCondensed]: variant === "condensed",
        })}
      >
        <div className={styles.ConversationMessageRowMessageActionText}>
          <div>
            <Text
              tagName="div"
              size={variant === "condensed" ? "medium" : "large"}
            >
              {message.title}
            </Text>
            <Text tagName="div" color="subtle">
              {message.subtitle}
            </Text>
          </div>
        </div>
      </div>
    );
  },
);
ConversationMessageRowInstruction.displayName =
  "ConversationMessageRowInstruction";

type ConversationMessageRowMessageProps =
  ConversationMessageRowGenericProps<ChatConversationMessageMessage>;
export const ConversationMessageRowMessage = memo(
  ({
    variant,
    message,
    isLastMessage,
    progressStepState,
    logComposeEvent,
    renderActions,
    onOpenArtifact,
    showReactions = true,
    hideSources = false,
    conversationId,
    searchSessionId,
    assistRequestId,
    spanId,
    lastUserMessage,
    searchResultsCache,
    overrideMarkdownRenderer,
    showDraftGeneratedButton,
    isWaitingForResponse,
  }: ConversationMessageRowMessageProps) => {
    const { searchQueryUuid } = useSearchQueryId();
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const hasLoggedImpression = useRef(false);
    const showButtonsForLegacyMessages = message.actionContext?.type === "done";
    const showButtonsForNewMessages =
      isLastMessage && message.role === "assistant" && !message.actionContext;
    // Only show action buttons when the conversation has been saved to the server (i.e. has a
    // `conversationId`). For unsaved/local conversations we never show the buttons.
    const showMessageActionButtons =
      showReactions &&
      Boolean(conversationId) &&
      (showButtonsForLegacyMessages || showButtonsForNewMessages);
    const isWriteDraft = message.actionContext?.type === "draft_generated";
    const stepMessageClassName = useStepMessageTransitionClass(
      message,
      progressStepState,
    );
    const useConversationFeedbackModal = useFeatureFlagValueBool(
      "dash_web_2025_06_04_updated_conversation_feedback",
    );
    const showSearchResultTiles =
      useFeatureFlagValue("dash_2025_06_10_search_in_chat", false) === "V2";
    const showSearchResultList =
      useFeatureFlagValue("dash_2025_06_10_search_in_chat", false) === "V1";

    const [searchResultsPlacement, setSearchResultsPlacement] = useState<
      "hidden" | "top" | "bottom"
    >("hidden");

    const { citations, context } = useMemo(
      () => getCitationAndContextSources(message),
      [message],
    );

    // Grab search results for the previous user message if available
    useEffect(() => {
      if (
        message.role === "assistant" &&
        lastUserMessage?.type === "message" &&
        lastUserMessage?.text &&
        searchResultsCache
      ) {
        const cachedPromise = searchResultsCache.current.get(
          lastUserMessage.text,
        );
        if (cachedPromise) {
          cachedPromise
            .then((response) => {
              return void setSearchResultsPlacement(response || "hidden");
            })
            .catch(() => {
              // silently fail but log error
              logger.error(
                "Error retrieving search results from cache in chat",
              );
            });
        }
      }
    }, [message.role, lastUserMessage, searchResultsCache]);

    // Decide whether or not we should show search results above or below the assistant message
    const showSearchResults =
      (showSearchResultList || showSearchResultTiles) &&
      message.role === "assistant" && // only show sources for assistant messages
      !!message.additionalContext?.length; // only show sources if there are any to actually display

    const sources = citations.length > 0 ? citations : context;
    const sourceType: ChatSourceType =
      citations.length > 0
        ? ChatSourceType.Citation
        : context.length > 0
          ? ChatSourceType.Context
          : ChatSourceType.None;

    useEffect(() => {
      // Ensure we only log the impression for an assistant response exactly once
      if (
        isLastMessage &&
        message.role === "assistant" &&
        !hasLoggedImpression.current
      ) {
        logComposeEvent(
          PAP_Shown_AssistantResponse({
            answerString: message.text,
            conversationId,
            searchSessionId,
            searchQueryUuid,
            assistRequestId,
          }),
        );

        hasLoggedImpression.current = true;
      }
    }, [
      isLastMessage,
      message.role,
      message.text,
      logComposeEvent,
      conversationId,
      searchSessionId,
      searchQueryUuid,
      assistRequestId,
    ]);

    const query =
      lastUserMessage?.type === "message" ? lastUserMessage.text : undefined;

    const renderSearchResults = () => {
      return showSearchResultTiles ? (
        <AllSourcesTiles
          sources={message.additionalContext ?? []}
          query={query}
        />
      ) : (
        <AllSourcesList
          sources={message.additionalContext ?? []}
          query={query}
        />
      );
    };

    const { isSingleLine: isSingleLineMessage, containerRef: messageRowRef } =
      useIsSingleLine(message.role === "user");

    const messageActionButtons = showMessageActionButtons && conversationId && (
      <div>
        <ConversationMessageActions
          variant={variant}
          onClickFeedback={(feedback) => {
            logComposeEvent(
              PAP_Click_MessageAction({
                actionType: feedback,
                generatedQueryString: message.text,
                queryLength: message.text.length,
                searchSessionId,
                conversationId,
                assistRequestId,
                searchQueryUuid,
              }),
            );
            if (feedback === "negative" && showButtonsForNewMessages) {
              setShowFeedbackModal(true);
            }
          }}
          onCopyMessage={
            isWriteDraft
              ? undefined
              : () => {
                  let textToCopy = message.text;
                  if (
                    message.role === "assistant" &&
                    message.referencingSources &&
                    message.referencingSources.length > 0
                  ) {
                    // Replace all [^n] and [cN] with markdown links to the source
                    textToCopy = message.text.replace(
                      /\[(c\d+|\^\d+)\]/g,
                      (match, p1) => {
                        const citationIndex = p1.replace(/^\^/, "");
                        const source = message.referencingSources?.find(
                          (source) => source.index === citationIndex,
                        );
                        if (source) {
                          const parts = getSourceRowParts(source);
                          const title =
                            parts?.titleString || `Source ${citationIndex}`;
                          const url =
                            (parts?.mirageResult && parts.mirageResult.url) ||
                            "";
                          if (url) {
                            return `[${title}](${url})`;
                          } else {
                            return title;
                          }
                        }
                        return match; // fallback to original if not found
                      },
                    );
                  }
                  copyToClipboard(textToCopy);
                  logComposeEvent(
                    PAP_Click_MessageAction({
                      actionType: "copy",
                      generatedQueryString: message.text,
                      queryLength: message.text.length,
                      entryPoint: "assistant_ui", // (as opposed to via keyboard shortcut)
                      searchSessionId,
                      conversationId,
                      assistRequestId,
                      searchQueryUuid,
                    }),
                  );
                }
          }
        />
      </div>
    );

    const referencedSourcesElement = !hideSources &&
      message.actionContext?.type !== "dash_search_analyze" &&
      sources &&
      sources.length > 0 &&
      // Don't show sources for assistant messages while streaming responses
      !(
        message.role === "assistant" &&
        isWaitingForResponse &&
        isLastMessage
      ) && (
        <div
          className={classNames(styles.ConversationMessageSources, {
            [styles.ConversationMessageSourcesUser]: message.role === "user",
          })}
        >
          {message.role === "user" ? (
            sources.map((source: ChatSource, i) => (
              <ConversationMessageRowMessageSourceRow
                variant={variant}
                key={getSourceUUID(source) || i}
                source={source}
                logComposeEvent={logComposeEvent}
                conversationId={conversationId}
                searchSessionId={searchSessionId}
                assistRequestId={assistRequestId}
              />
            ))
          ) : (
            <ConversationMessageSource
              sources={sources}
              sourceType={sourceType}
              conversationId={conversationId}
              searchSessionId={searchSessionId}
              logComposeEvent={logComposeEvent}
              assistRequestId={assistRequestId}
            />
          )}
        </div>
      );
    const isEmptyUserMessage = message.role === "user" && !message.text;
    return (
      <>
        {showSearchResults &&
          searchResultsPlacement === "top" &&
          renderSearchResults()}

        {
          // for user, show newly added sources before the message
          message.role === "user" &&
          (referencedSourcesElement || messageActionButtons) ? (
            <div className={styles.ConversationMessageRowContainer}>
              {messageActionButtons}
              {referencedSourcesElement}
            </div>
          ) : null
        }
        {/* hide empty user messages, but show action context for empty assistant responses */}
        {!isEmptyUserMessage && (
          <div
            ref={messageRowRef}
            className={classNames(
              styles.ConversationMessageRow,
              meticulousRedactedStringClass,
              {
                [styles.ConversationMessageRowUser]: message.role === "user",
                [styles.ConversationMessageRowUserShort]: isSingleLineMessage,
                [styles.ConversationMessageRowCondensed]:
                  variant === "condensed",
              },
              stepMessageClassName,
            )}
            data-role={message.role}
          >
            <ConversationMessageRowActionAndText
              variant={variant}
              isLastMessage={isLastMessage}
              isInProgress={progressStepState === "waiting"}
              message={message}
              renderActions={renderActions}
              onOpenArtifact={onOpenArtifact}
              logComposeEvent={logComposeEvent}
              conversationId={conversationId}
              searchSessionId={searchSessionId}
              assistRequestId={assistRequestId}
              overrideMarkdownRenderer={overrideMarkdownRenderer}
              showDraftGeneratedButton={showDraftGeneratedButton}
              spanId={spanId}
            />
          </div>
        )}
        {
          // for assistant, show newly added sources after the message
          message.role !== "user" &&
          (referencedSourcesElement || messageActionButtons) ? (
            <div className={styles.ConversationMessageRowContainer}>
              {messageActionButtons}
              {referencedSourcesElement}
            </div>
          ) : null
        }

        {showSearchResults &&
          searchResultsPlacement === "bottom" &&
          renderSearchResults()}

        {showButtonsForNewMessages &&
          conversationId &&
          (useConversationFeedbackModal ? (
            <ConversationFeedbackModal
              conversationId={conversationId}
              isOpen={showFeedbackModal}
              logComposeEvent={logComposeEvent}
              onClose={() => setShowFeedbackModal(false)}
              spanId={spanId}
            />
          ) : (
            <AnswersAnnotationModal
              isOpen={showFeedbackModal}
              onClose={() => setShowFeedbackModal(false)}
              query={message.text}
              answer={{
                answer: message.text,
                sources: (sources || [])
                  .map(composeSourceToMirage)
                  .filter(Boolean) as (
                  | SearchResult
                  | QuestionAndAnswerSource
                )[],
                conversationId,
                searchRequestId: searchSessionId,
                answerId: assistRequestId,
              }}
              reportPAPEvent={(event) => {
                logComposeEvent(event);
              }}
              searchSessionId={searchSessionId}
              conversationId={conversationId}
            />
          ))}
      </>
    );
  },
);
ConversationMessageRowMessage.displayName = "ConversationMessageRowMessage";
